import { Task } from '../types';
import { useTaskStore } from '../store/taskStore';
import { Check, Trash2, Image as ImageIcon, Eye, MessageCircle } from 'lucide-react';
import { useState } from 'react';

interface TaskCardProps {
  task: Task;
}

export default function TaskCard({ task }: TaskCardProps) {
  const { toggleTask, deleteTask, setSelectedTask } = useTaskStore();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await toggleTask(task.id);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Bu görevi silmek istediğinizden emin misiniz?')) return;
    
    setIsDeleting(true);
    try {
      await deleteTask(task.id);
    } catch (error) {
      setIsDeleting(false);
    }
  };

  const handleView = () => {
    setSelectedTask(task);
  };

  const imageCount = task.images?.length || 0;
  const commentCount = task.comment_count || 0;

  return (
    <div
      onClick={handleView}
      className={`bg-white rounded-soft shadow-soft hover:shadow-soft-lg transition-all cursor-pointer p-6 ${
        task.completed ? 'opacity-75' : ''
      } ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <button
          onClick={handleToggle}
          className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
            task.completed
              ? 'bg-primary-500 border-primary-500'
              : 'border-gray-300 hover:border-primary-500'
          }`}
        >
          {task.completed && <Check className="w-4 h-4 text-white" />}
        </button>

        <button
          onClick={handleDelete}
          className="flex-shrink-0 text-soft-muted hover:text-red-500 transition-colors"
          title="Görevi sil"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Title */}
      <h3
        className={`text-lg font-semibold mb-2 ${
          task.completed ? 'line-through text-soft-muted' : 'text-gray-900'
        }`}
      >
        {task.title}
      </h3>

      {/* Description */}
      {task.description && (
        <p className="text-soft-muted text-sm mb-4 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Created By */}
      {task.created_by && (
        <p className="text-soft-muted text-xs mb-4 flex items-center gap-1">
          <span className="font-medium">Oluşturan:</span>
          <span>{task.created_by}</span>
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-soft-border">
        {/* Badges */}
        <div className="flex items-center gap-3">
          {/* Image count */}
          {imageCount > 0 && (
            <div className="flex items-center gap-1 text-soft-muted text-sm">
              <ImageIcon className="w-4 h-4" />
              <span>{imageCount}</span>
            </div>
          )}

          {/* Comment count */}
          {commentCount > 0 && (
            <div className="flex items-center gap-1 text-primary-500 text-sm">
              <MessageCircle className="w-4 h-4" />
              <span>{commentCount}</span>
            </div>
          )}
        </div>

        {/* View button */}
        <button
          onClick={handleView}
          className="flex items-center gap-1 text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors"
        >
          <Eye className="w-4 h-4" />
          Detay
        </button>
      </div>
    </div>
  );
}
